/**
 * Diálogo de invitación con ROL + SEDE (F3-D) — reutiliza el flujo de
 * invitación de F1-A (`POST /api/org/inviteMember`, que acepta `role`/`campusId`)
 * añadiendo los selectores de rol y sede. El servidor valida el rol asignable y
 * la pertenencia de la sede a la org.
 */
import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Mail, UserPlus } from "lucide-solid";
import { generateUUID, handleApiResponse } from "@space/shared";
import type { Campus } from "@space/powersync";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";

const ROLE_OPTIONS: { value: string; label: MessageDescriptor }[] = [
  { value: "student", label: msg`Student` },
  { value: "teacher", label: msg`Teacher` },
  { value: "staff", label: msg`Staff` },
  { value: "guardian", label: msg`Guardian` },
  { value: "campus_admin", label: msg`Campus admin` },
  { value: "org_admin", label: msg`Org admin` },
];

interface PeopleInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  campuses: Campus[];
  /** Rol pre-seleccionado (según la pestaña activa del directorio). */
  defaultRole?: string;
  onInvited?: () => void;
}

export function PeopleInviteDialog(props: PeopleInviteDialogProps) {
  const { t } = useI18n();
  const [email, setEmail] = createSignal("");
  const [role, setRole] = createSignal(props.defaultRole ?? "student");
  const [campusId, setCampusId] = createSignal<string>("");
  const [submitting, setSubmitting] = createSignal(false);

  const campusOptions = () => [
    { value: "", label: t(msg`All campuses`) },
    ...props.campuses.map((c) => ({ value: c.id, label: c.name ?? c.id })),
  ];

  const submit = async (e: Event) => {
    e.preventDefault();
    const mail = email().trim();
    if (!mail || submitting()) {
      return;
    }
    setSubmitting(true);
    try {
      await handleApiResponse(
        api.POST("/api/org/inviteMember", {
          body: {
            id: generateUUID(),
            organizationId: props.organizationId,
            email: mail,
            role: role(),
            campusId: campusId() || null,
          },
        }),
      );
      showToast(t(msg`Invitation sent`), "success");
      setEmail("");
      props.onInvited?.();
      props.onOpenChange(false);
    } catch {
      showToast(t(msg`Could not send the invitation`), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <UserPlus class="h-5 w-5" />
          </div>
          <DialogTitle class="text-xl font-bold">{t(msg`Invite to the academy`)}</DialogTitle>
        </div>
      </DialogHeader>

      <form onSubmit={(e) => void submit(e)} class="space-y-5 p-6" data-testid="people-invite-form">
        <DialogDescription class="text-sm text-muted-foreground">
          {t(msg`Send an email invitation with a role and (optionally) a campus.`)}
        </DialogDescription>

        <div class="space-y-2">
          <Label for="invite-email" class="text-xs font-bold uppercase tracking-wider opacity-50">
            {t(msg`Email address`)}
          </Label>
          <div class="relative">
            <Mail class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <TextField
              id="invite-email"
              type="email"
              data-testid="people-invite-email"
              value={email()}
              onInput={(e) => setEmail(e.currentTarget.value)}
              placeholder={t(msg`example@email.com`)}
              autofocus
              class="h-12 rounded-xl border-border/50 bg-muted/50 pl-10"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-2">
            <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
              {t(msg`Role`)}
            </Label>
            <Select
              options={ROLE_OPTIONS.map((r) => ({ value: r.value, label: t(r.label) }))}
              value={role()}
              onChange={setRole}
              aria-label={t(msg`Role`)}
            />
          </div>
          <div class="space-y-2">
            <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
              {t(msg`Campus`)}
            </Label>
            <Select
              options={campusOptions()}
              value={campusId()}
              onChange={setCampusId}
              aria-label={t(msg`Campus`)}
            />
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            class="h-11 rounded-xl px-6"
            onClick={() => props.onOpenChange(false)}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button
            type="submit"
            data-testid="people-invite-submit"
            disabled={submitting() || !email().trim()}
            class="h-11 gap-2 rounded-xl px-8 font-bold"
          >
            <Show when={submitting()}>
              <div class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </Show>
            {t(msg`Send invitation`)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
